import {inject, Injectable} from '@angular/core';
import {SnackBarService} from './snack-bar.service';
import {LOCAL_STORAGE_JWT_KEY} from "./auth/auth.service";
import {APP_CONFIG} from "./app.config";

/**
 * Handle and display errors.
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  private snackBarService = inject(SnackBarService);
  private appConfig = inject(APP_CONFIG);

  /**
   * Known error codes returned by the backend (see ErrorCode.java).
   * Each code maps to a translation key under "ERROR.<code>".
   */
  private static readonly KNOWN_ERROR_CODES = new Set([
    'INVALID_TDS_DATA',
    'VIN_MUST_NOT_BE_CHANGED',
    'VIN_NOT_UNIQUE',
    'EXPECTED_VEHICLE_ENTRY_DATE_IN_PAST',
    'SALES_PRICE_REQUIRED',
    'NEW_SALES_TOTAL_PRICE_MUST_NOT_BE_NULL',
    'VEHICLE_DOES_NOT_EXIST',
    'INVALID_CAR_HUB_DATA',
    'INVALID_UPDATE_DATA',
    'INVALID_DATA',
    'COULD_NOT_LOAD_LOGGED_IN_DEALER',
    'EXTERNAL_SYSTEM_UNAVAILABLE',
    'NO_VEHICLES_FOR_PRINT',
    'VEHICLES_NOT_FOUND',
    'VEHICLE_NOT_FOUND_IN_EXTERNAL_SYSTEM',
    'AUTHENTICATION_REQUIRED',
    'FORBIDDEN_UCMIM_ACCESS',
    'INVALID_DEALER',
    'DEALER_TO_DELETE_HAS_VEHICLES',
  ]);

  public handleError(err: any): void {
    if (err?.status === 401 && err.error?.message === 'JWT_SIGNATURE_FAILED') {
      localStorage.removeItem(LOCAL_STORAGE_JWT_KEY);
      window.open(this.appConfig.loginUrl, '_self');
      return;
    }

    if (err?.status === 401 && err.error?.message === 'JWT_AUTH_FAILED') {
      this.showErrorSimple(err, 'ERROR.JWT_AUTH_FAILED');
      return;
    }

    const errorCode = this.extractErrorCode(err?.error);
    if (errorCode) {
      this.showErrorSimple(err, `ERROR.${errorCode}`);
    } else {
      this.showErrorSimple(err, 'ERROR.GENERAL');
    }
  }

  /**
   * Extracts a known error code from the response body.
   * The backend returns error bodies in the format "<status_code> <ERROR_CODE>"
   * (e.g. "400 INVALID_TDS_DATA", "404 VEHICLE_DOES_NOT_EXIST").
   * Some codes may have a suffix (e.g. "400 VIN_NOT_UNIQUE: ABC123").
   */
  private extractErrorCode(error: unknown): string | null {
    if (typeof error !== 'string') {
      return null;
    }
    const match = error.match(/^\d+\s+(.*)/);
    if (!match) {
      return null;
    }
    const errorPart = match[1];
    for (const code of ErrorService.KNOWN_ERROR_CODES) {
      if (errorPart === code || errorPart.startsWith(code + ':')) {
        return code;
      }
    }
    return null;
  }

  private showErrorSimple(err: any, code: string, messageParams?: any) {
    console.error('An error occured: ', code, err);
    this.snackBarService.showError(code, messageParams);
  }
}
